import { TST_COLUMN_SIZE } from "@impero-com/ui";
import { Column, ColumnHelper } from "@tanstack/react-table";

import { UserTagCategoryOverview, UserTagOverview } from "api/generated/model";
import {
    formatTagsAndCategoriesString,
    tagAndCategoryListDefaultTSTCell,
    tagsTSTFilterFn,
    tagsTSTSortingFn,
} from "org/tags/tagsTSTUtils";
import { createFilterFnWithMode } from "utils/table/createFilterFnWithMode";

export function getUniqueTagIds<TRow>(
    column: Column<TRow>,
    selector: (row: TRow) => string[]
): string[] {
    const tagIdsPerRow = column
        .getFacetedRowModel()
        .flatRows.map((row) => selector(row.original));
    return Array.from(new Set(tagIdsPerRow.flat()));
}

interface TagColumnOptions<TRow> {
    id: string;
    header: string;
    /** Reads the tag ids for a row (e.g. `(row) => row.samplingTagIds`). */
    selector: (row: TRow) => string[];
    tags: UserTagOverview[];
    tagCategories: UserTagCategoryOverview[];
}

export function buildTagColumn<TRow>(
    columnHelper: ColumnHelper<TRow>,
    { id, header, selector, tags, tagCategories }: TagColumnOptions<TRow>
) {
    return columnHelper.accessor(
        (row: TRow) =>
            formatTagsAndCategoriesString(selector(row), {
                tags,
                tagCategories,
            }),
        {
            id,
            cell: (info) =>
                tagAndCategoryListDefaultTSTCell(
                    selector(info.row.original),
                    tags,
                    tagCategories
                ),
            enableSorting: true,
            sortingFn: (rowA, rowB) =>
                tagsTSTSortingFn(
                    selector(rowA.original),
                    selector(rowB.original)
                ),
            enableColumnFilter: true,
            filterFn: createFilterFnWithMode<TRow, string[]>((row, value) =>
                tagsTSTFilterFn(selector(row.original), value)
            ),
            header: () => header,
            size: TST_COLUMN_SIZE.L,
        }
    );
}
